import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Dropdown, Grid, Message } from 'semantic-ui-react';

import {
  SelectWrapper,
  ButtonsWrapper,
  DateFieldPrefix,
  DateFieldsWrapper,
  ReportSectionWrapper
} from '../../styles';
import Button from '../../../../components/Button';
import { FieldLabel } from '../../../../styles';
import { iRootDispatch, iRootState } from '../../../../store';
import { connect } from 'react-redux';
import { Props } from './types';
import {
  dangerNotification,
  successNotification,
  warningNotification
} from '../../../../services/notification';
import CustomDatePicker from '../../../../components/CustomDatePicker';
import AppMaskedInput from '../../../../components/MaskedInput';
import { DateToStringDate } from '../../../../services/date';
import { dateFromString } from '../../helpers';
import { AvailableBrokersAndEstatesState } from '../../../../store/types/temp-types';
import { get } from '../../../../services/request';
import EstateUnitFilter from '../../../../components/EstateUnitFilter';
import {
  buildEstateUnitQueryString,
  resolveEstateIdForUnitFilter
} from '../../../../utils/estateUnitFilter.util';

const dateMaskInput = (
  <AppMaskedInput
    mask={[
      /[0-9]/,
      /[0-9]/,
      '/',
      /[0-9]/,
      /[0-9]/,
      '/',
      /[0-9]/,
      /[0-9]/,
      /[0-9]/,
      /[0-9]/
    ]}
  />
);

const GeneralProductionReport: React.FC<Props> = ({
  role,
  closeSection,
  availableBrokersAndEstates,
  user
}) => {
  const [selectedEstatesValues, setSelectedEstatesValues] = useState<string>();
  const [estateUnitIdFilter, setEstateUnitIdFilter] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [isExportLoading, setIsExportLoading] = useState(false);

  const estateOptions = useMemo(
    () =>
      availableBrokersAndEstates.estates?.map(option => ({
        text: option.text,
        value: option.value.id as string
      })) || [],
    [availableBrokersAndEstates.estates]
  );

  useEffect(() => {
    if (role.includes('ESTATE') && estateOptions.length > 0) {
      const activeId = availableBrokersAndEstates.activeEstateId;
      const matchingOption = activeId
        ? estateOptions.find(o => o.value === activeId)
        : null;
      setSelectedEstatesValues(matchingOption?.value || estateOptions[0].value);
    }
  }, [role, estateOptions, availableBrokersAndEstates.activeEstateId]);

  const formattedStart = useMemo(() => dateFromString(start), [start]);
  const formattedEnd = useMemo(() => dateFromString(end), [end]);

  const onExportButtonClick = useCallback(async () => {
    if (!start || !end) {
      warningNotification('Erro', 'Por favor, selecione um período válido.');
      return;
    }

    if (formattedStart && formattedEnd && formattedStart > formattedEnd) {
      warningNotification(
        'Erro',
        'O período de início não pode ser maior que o período de término.'
      );
      return;
    }

    if (formattedStart && formattedEnd && formattedStart > formattedEnd) {
      warningNotification(
        'Erro',
        'O período de início não pode ser maior que o período de término.'
      );
      return;
    }

    setIsExportLoading(true);

    let apiStartDate = '';
    let apiEndDate = '';

    if (start) {
      const [day, month, year] = start.split('/');
      apiStartDate = `${year}-${month}-${day}`;
    }

    if (end) {
      const [day, month, year] = end.split('/');
      apiEndDate = `${year}-${month}-${day}`;
    }

    try {
      await get(
        `/reports/general-production/xlsx?isRv=true&start=${apiStartDate}&end=${apiEndDate}&${selectedEstatesValues ? `estateId=${selectedEstatesValues}` : ''}${buildEstateUnitQueryString(estateUnitIdFilter)}`
      );

      successNotification(
        'Sucesso!',
        'Relatório solicitado com sucesso, você receberá um e-mail com o arquivo em breve.'
      );
    } catch (error) {
      console.log(error);
      dangerNotification(
        'Oops...',
        'Não foi possível exportar o relatório de produção geral, tente novamente mais tarde.'
      );
    } finally {
      setIsExportLoading(false);
    }
  }, [start, end, selectedEstatesValues, estateUnitIdFilter]);

  const handleEstateChange = useCallback((_, data) => {
    setSelectedEstatesValues(data.value);
  }, []);

  const handleStartDateChange = useCallback((value: Date) => {
    setStart(DateToStringDate(value));
  }, []);

  const handleEndDateChange = useCallback((value: Date) => {
    setEnd(DateToStringDate(value));
  }, []);

  return (
    <ReportSectionWrapper>
      <Message info>
        <Message.Header>Relatório de Produção Geral</Message.Header>
        <p>Este relatório unificado contém três abas diferentes com informações sobre:</p>
        <ul>
          <li>Fianças emitidas</li>
          <li>Incêndios emitidos</li>
          <li>Capitalizações emitidas</li>
        </ul>
        <p>O arquivo será enviado para o seu e-mail após o processamento.</p>
      </Message>

      <Grid>
        <Grid.Row style={{ paddingTop: 0, paddingBottom: 0 }}>
          <Grid.Column width={8}>
            {!role.includes('ESTATE') && (
              <>
                <FieldLabel paddingBottom>Imobiliária</FieldLabel>

                <SelectWrapper style={{ marginBottom: 0 }}>
                  <Dropdown
                    id="realEstatesSelect"
                    placeholder="Todas imobiliárias"
                    value={selectedEstatesValues}
                    options={estateOptions || []}
                    onChange={handleEstateChange}
                    style={{ marginBottom: '5px' }}
                    menu={{ style: { maxHeight: '300px', overflowY: 'auto' } }}
                    noResultsMessage={'Carregando...'}
                    clearable
                    fluid
                    search
                    selection
                  />
                </SelectWrapper>
              </>
            )}
          </Grid.Column>
          <Grid.Column width={8}>
            <EstateUnitFilter
              estateId={resolveEstateIdForUnitFilter(
                selectedEstatesValues,
                availableBrokersAndEstates.activeEstateId ?? undefined
              )}
              value={estateUnitIdFilter}
              onChange={setEstateUnitIdFilter}
            />
          </Grid.Column>
        </Grid.Row>

        <Grid.Row style={{ paddingTop: '0', paddingBottom: '0' }}>
          <Grid.Column width={12}>
            <FieldLabel>Período*</FieldLabel>

            <DateFieldsWrapper>
              <div>
                <DateFieldPrefix>De</DateFieldPrefix>

                <CustomDatePicker
                  value={start}
                  onChange={handleStartDateChange}
                  maxDate={formattedEnd || undefined}
                  customInput={dateMaskInput}
                />
              </div>

              <div>
                <DateFieldPrefix>Até</DateFieldPrefix>

                <CustomDatePicker
                  value={end}
                  onChange={handleEndDateChange}
                  minDate={formattedStart || undefined}
                  customInput={dateMaskInput}
                />
              </div>
            </DateFieldsWrapper>
          </Grid.Column>
        </Grid.Row>
      </Grid>

      <ButtonsWrapper>
        <Button
          type="button"
          text="Cancelar"
          disabled={isExportLoading}
          onClick={closeSection}
          light
        />

        <Button
          type="button"
          text="Solicitar relatório"
          disabled={isExportLoading}
          onClick={onExportButtonClick}
        />
      </ButtonsWrapper>
    </ReportSectionWrapper>
  );
};

const mapStateToProps = (state: iRootState) => ({
  insurers: state.availableBrokersAndEstates.insurers,
  availableBrokersAndEstates: state.availableBrokersAndEstates,
  user: state.user
});

const mapDispatch = (dispatch: iRootDispatch) => ({
  updateAvailableBrokersAndEstates: (availableBrokers: AvailableBrokersAndEstatesState) =>
    dispatch.availableBrokersAndEstates.updateAvailableBrokersAndEstates(availableBrokers)
});

export default connect(mapStateToProps, mapDispatch)(GeneralProductionReport);
